import React, { useState, useEffect, useMemo } from "react";
import { 
  LayoutDashboard, 
  Boxes, 
  ClipboardList, 
  CheckCircle, 
  Calendar, 
  Settings, 
  ToggleLeft, 
  ToggleRight,
  TrendingUp,
  Package,
  AlertTriangle,
  Clock
} from "lucide-react";
import StockTab from "./StockTab";
import StockProductionTab from "./StockProductionTab";
import PlanningTab from "./PlanningTab";
import LotesTab from "./LotesTab";
import MotivosConfigTab from "./MotivosConfigTab";
import FabricaSubTab from "./FabricaSubTab";
import SolicitacoesSubTab from "./SolicitacoesSubTab";
import AlertasSubTab from "./AlertasSubTab";
import { pb } from "../services/pocketbase";
import { getActiveWorkshopLoad } from "../utils/skuUtils";

export default function EstoqueAreaTab({ 
  user, 
  findCatalogItem, 
  inventory,
  reservations,
  loadingCatalog,
  onRefreshCatalog,
  hasAccess,
  barcodeInput,
  setBarcodeInput,
  inputRef,
  handleScanToBuffer,
  scannedBuffer,
  setScannedBuffer,
  isCommitting,
  handleCommitBuffer,
  salesInput,
  setSalesInput,
  processSales,
  toggleModoClassico
}) {
  const [activeSubTab, setActiveSubTab] = useState("cockpit");
  const [lotes, setLotes] = useState([]);
  const [loadingLotes, setLoadingLotes] = useState(false);

  // Carrega os lotes para o Cockpit
  const loadCockpitLotes = async () => {
    try {
      setLoadingLotes(true);
      const res = await pb.collection("estoque_lotes").getFullList({
        sort: "-created",
        limit: 10
      });
      setLotes(res);
    } catch (e) {
      console.error("Erro ao carregar lotes para o Cockpit:", e);
    } finally {
      setLoadingLotes(false);
    }
  };

  useEffect(() => {
    if (activeSubTab === "cockpit") {
      loadCockpitLotes();
    }
  }, [activeSubTab]);

  // Cálculos de KPIs do Cockpit
  const cockpitKpis = useMemo(() => {
    const prontas = inventory.filter(i => i.status === "in_stock" && !i.isPE).length;
    
    // Carga real da fábrica usando a fórmula canônica (S3.1 e S3.2)
    const emProducao = getActiveWorkshopLoad(inventory);
    
    const activeProductionItems = inventory.filter(i => 
      i.isPE && 
      !["in_stock", "sold", "adjusted_out", "pe_interceptado"].includes(i.status) &&
      i.situacao_fluxo !== "suspenso" &&
      i.situacao_fluxo !== "interceptado" &&
      i.status !== "pe_pre_escapulario"
    );

    let custoProducao = 0;
    activeProductionItems.forEach(item => {
      const cat = findCatalogItem ? findCatalogItem(item.sku) : null;
      const unitCost = Number(cat?.cost || cat?.preco_custo || cat?.full_data?.custo || 0);
      custoProducao += unitCost;
    });

    const lotesAtivos = lotes.filter(l => l.status === "em_producao").length;
    const lotesPendentes = lotes.filter(l => l.status === "aguardando_aprovacao").length;

    return { prontas, emProducao, custoProducao, lotesAtivos, lotesPendentes };
  }, [inventory, lotes, findCatalogItem]);

  return (
    <div className="flex flex-col h-full w-full bg-slate-50 overflow-hidden">
      {/* Sub-navbar superior */}
      <div className="bg-white border-b border-slate-200 px-4 py-2.5 flex flex-wrap items-center justify-between gap-4 shrink-0 shadow-sm">
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-none">
          <button
            onClick={() => setActiveSubTab("cockpit")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
              activeSubTab === "cockpit" 
                ? "bg-slate-900 text-white shadow-sm" 
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            }`}
          >
            <LayoutDashboard size={14} /> Cockpit
          </button>
          
          <button
            onClick={() => setActiveSubTab("fabrica")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
              activeSubTab === "fabrica" 
                ? "bg-slate-900 text-white shadow-sm" 
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            }`}
          >
            <Boxes size={14} /> Fábrica
          </button>

          <button
            onClick={() => setActiveSubTab("estoque_pronto")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
              activeSubTab === "estoque_pronto" 
                ? "bg-slate-900 text-white shadow-sm" 
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            }`}
          >
            <ClipboardList size={14} /> Estoque Pronto
          </button>

          <button
            onClick={() => setActiveSubTab("solicitacoes")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
              activeSubTab === "solicitacoes" || activeSubTab === "aprovacoes"
                ? "bg-slate-900 text-white shadow-sm" 
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            }`}
            title="Central unificada de solicitações de lotes (Planejamento, Oficina e Manual)"
          >
            <ClipboardList size={14} /> Solicitações
          </button>

          <button
            onClick={() => setActiveSubTab("alertas")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
              activeSubTab === "alertas" 
                ? "bg-slate-900 text-white shadow-sm" 
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            }`}
            title="Central de Alertas e Níveis Mínimos"
          >
            <AlertTriangle size={14} /> Alertas
          </button>

          <button
            onClick={() => setActiveSubTab("planejamento")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
              activeSubTab === "planejamento" 
                ? "bg-slate-900 text-white shadow-sm" 
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            }`}
          >
            <Calendar size={14} /> Planejamento
          </button>

          {user?.role === "master" && (
            <button
              onClick={() => setActiveSubTab("configuracoes")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                activeSubTab === "configuracoes" 
                  ? "bg-slate-900 text-white shadow-sm" 
                  : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
              }`}
            >
              <Settings size={14} /> Configurações
            </button>
          )}
        </div>

        {/* Toggle do Modo Clássico */}
        <button
          onClick={() => toggleModoClassico(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 hover:border-slate-300 text-slate-500 hover:text-slate-800 transition-all text-xs font-bold bg-slate-50 hover:bg-slate-100 cursor-pointer shadow-sm"
          title="Ativar visualização clássica com links individuais no menu lateral"
        >
          <ToggleLeft size={16} className="text-blue-500" />
          Modo Clássico
        </button>
      </div>

      {/* Conteúdo da Aba Ativa */}
      <div className="flex-1 overflow-auto">
        {activeSubTab === "cockpit" && (
          <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto animate-fade-in">
            {/* Top row cards - 5 KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Estoque Pronto</span>
                  <p className="text-xl font-black mt-1 text-slate-800">{cockpitKpis.prontas} pçs</p>
                </div>
                <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-lg">
                  <Package size={20} />
                </div>
              </div>

              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Em Produção</span>
                  <p className="text-xl font-black mt-1 text-blue-600">{cockpitKpis.emProducao} pçs</p>
                  <span className="text-[10px] text-slate-400 font-bold block mt-0.5">
                    {cockpitKpis.custoProducao > 0 ? `R$ ${cockpitKpis.custoProducao.toFixed(2)}` : "Sem custo inf."}
                  </span>
                </div>
                <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg">
                  <Clock size={20} />
                </div>
              </div>

              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Lotes Ativos</span>
                  <p className="text-xl font-black mt-1 text-purple-600">{cockpitKpis.lotesAtivos}</p>
                </div>
                <div className="p-2.5 bg-purple-50 text-purple-600 rounded-lg">
                  <Boxes size={20} />
                </div>
              </div>

              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Buffer Aprovações</span>
                  <p className={`text-xl font-black mt-1 ${cockpitKpis.lotesPendentes > 0 ? "text-amber-600 animate-pulse" : "text-slate-800"}`}>
                    {cockpitKpis.lotesPendentes} lotes
                  </p>
                </div>
                <div className={`p-2.5 rounded-lg ${cockpitKpis.lotesPendentes > 0 ? "bg-amber-50 text-amber-600" : "bg-slate-50 text-slate-400"}`}>
                  <AlertTriangle size={20} />
                </div>
              </div>

              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Pipeline Rascunho</span>
                  <p className="text-xl font-black mt-1 text-slate-700">
                    {lotes.filter(l => l.status === "rascunho").length} lotes
                  </p>
                </div>
                <div className="p-2.5 bg-slate-100 text-slate-600 rounded-lg">
                  <TrendingUp size={20} />
                </div>
              </div>
            </div>

            {/* Painel Central do Cockpit com Ações Rápidas */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden p-5">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-5 pb-4 border-b border-slate-100">
                <div>
                  <h3 className="text-sm font-black text-slate-800">Cockpit Geral de Operações de Estoque</h3>
                  <p className="text-xs text-slate-400">Visão consolidada da fábrica, buffer de governança e estoque pronto.</p>
                </div>
                
                {/* Atalhos Rápidos */}
                <div className="flex flex-wrap items-center gap-2">
                  {cockpitKpis.lotesPendentes > 0 && (
                    <button
                      onClick={() => setActiveSubTab("solicitacoes")}
                      className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-bold shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
                    >
                      <CheckCircle size={14} /> Ver Solicitações Pendentes ({cockpitKpis.lotesPendentes})
                    </button>
                  )}
                  <button
                    onClick={() => setActiveSubTab("planejamento")}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
                  >
                    <Calendar size={14} /> Planejador de Demandas
                  </button>
                  <button 
                    onClick={() => setActiveSubTab("fabrica")}
                    className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
                  >
                    <Boxes size={14} /> Ver Fábrica &rarr;
                  </button>
                </div>
              </div>

              {/* Lotes em andamento rápido no Cockpit */}
              <div className="mt-2">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Lotes Ativos Recentes na Fábrica</h4>
                {loadingLotes ? (
                  <p className="text-xs text-slate-400 italic">Carregando lotes da fábrica...</p>
                ) : lotes.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">Nenhum lote cadastrado no momento.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {lotes.slice(0, 6).map(l => (
                      <div key={l.id} className="border border-slate-200 p-4 rounded-xl hover:shadow-md transition-all flex flex-col justify-between bg-slate-50/50">
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-extrabold text-xs text-slate-800">{l.codigo}</span>
                            <span className="text-[10px] text-slate-400 font-medium uppercase">({l.origem})</span>
                          </div>
                          <span className={`text-[9px] uppercase font-bold px-2 py-0.5 rounded ${
                            l.status === "em_producao" ? "bg-blue-100 text-blue-800 border border-blue-200" :
                            l.status === "concluido" ? "bg-emerald-100 text-emerald-800 border border-emerald-200" :
                            l.status === "aguardando_aprovacao" ? "bg-amber-100 text-amber-800 border border-amber-200" : "bg-slate-100 text-slate-600 border border-slate-200"
                          }`}>
                            {l.status}
                          </span>
                        </div>
                        {l.observacoes && <p className="text-[11px] text-slate-500 italic truncate mb-2">Obs: {l.observacoes}</p>}
                        <div className="flex justify-between items-center pt-2 border-t border-slate-100 text-[10px] text-slate-400">
                          <span>Criado em: {new Date(l.created).toLocaleDateString("pt-BR")}</span>
                          <button 
                            onClick={() => setActiveSubTab("fabrica")}
                            className="text-blue-600 font-bold hover:underline flex items-center gap-0.5"
                          >
                            Ver detalhes &rarr;
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeSubTab === "fabrica" && (
          <FabricaSubTab 
            user={user} 
            findCatalogItem={findCatalogItem} 
            inventory={inventory} 
            onRefresh={onRefreshCatalog}
          />
        )}

        {activeSubTab === "estoque_pronto" && (
          <StockTab
            inventory={inventory}
            reservations={reservations}
            findCatalogItem={findCatalogItem}
            user={user}
            loadingCatalog={loadingCatalog}
            onRefreshCatalog={onRefreshCatalog}
            hasAccess={hasAccess}
            barcodeInput={barcodeInput}
            setBarcodeInput={setBarcodeInput}
            inputRef={inputRef}
            handleScanToBuffer={handleScanToBuffer}
            scannedBuffer={scannedBuffer}
            setScannedBuffer={setScannedBuffer}
            isCommitting={isCommitting}
            handleCommitBuffer={handleCommitBuffer}
            salesInput={salesInput}
            setSalesInput={setSalesInput}
            processSales={processSales}
          />
        )}

        {(activeSubTab === "solicitacoes" || activeSubTab === "aprovacoes") && (
          <SolicitacoesSubTab 
            user={user} 
            inventory={inventory} 
            findCatalogItem={findCatalogItem} 
            onRefresh={onRefreshCatalog}
          />
        )}

        {activeSubTab === "alertas" && (
          <AlertasSubTab 
            user={user} 
            inventory={inventory} 
            reservations={reservations} 
            findCatalogItem={findCatalogItem} 
            onRefresh={onRefreshCatalog}
          />
        )}

        {activeSubTab === "planejamento" && (
          <PlanningTab 
            user={user} 
            inventory={inventory} 
          />
        )}

        {activeSubTab === "configuracoes" && (
          <div className="p-4 md:p-6 max-w-7xl mx-auto h-full flex flex-col">
            <div className="mb-4">
              <h3 className="text-sm font-black text-slate-800">Configurações Locais de Estoque</h3>
              <p className="text-xs text-slate-400">Configure motivos de descarte e suspensão aplicáveis à produção de pronta entrega.</p>
            </div>
            <div className="flex-1 min-h-0 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden p-4">
              <MotivosConfigTab user={user} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
